// ── Entity Excel import dialog ───────────────────────────────────────────────
// Drop an .xlsx, preview every row (new / skipped and why), then write the
// new companies in batches. The column spec, parser, sample and export live
// in utils/entityExcel.ts.

import { useMemo, useRef, useState } from 'react';
import { collection, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { Button, Modal } from './ui';
import { TYPE_LABEL, parseEntitiesXlsx, downloadEntitySample, type ParsedEntity } from '../utils/entityExcel';
import type { Entity } from '../types';

// ── Import modal ────────────────────────────────────────────────────────────

function IconUploadFile() {
  return <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M12 17v-6" /><path d="M9.5 13.5 12 11l2.5 2.5" />
  </svg>;
}

export function ImportEntitiesModal({ open, onClose, existing, onImported }: {
  open: boolean;
  onClose: () => void;
  existing: Entity[];
  onImported: (count: number) => void;
}) {
  const { user, business } = useAuth();
  const [fileName, setFileName] = useState('');
  const [parsed,   setParsed]   = useState<{ rows: ParsedEntity[]; warnings: string[] } | null>(null);
  const [busy,     setBusy]     = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error,    setError]    = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  // Reset is handled by remounting (key={} at the call site).

  async function readFile(f: File) {
    setError('');
    if (!/\.xlsx$/i.test(f.name)) { setError('Please choose an Excel (.xlsx) file — start from the sample file.'); return; }
    setBusy(true);
    try {
      const res = await parseEntitiesXlsx(await f.arrayBuffer(), existing);
      setFileName(f.name);
      setParsed(res);
      if (res.rows.length === 0) setError('No companies found in that file.');
    } catch {
      setError('Could not read that file. Make sure it is an .xlsx saved from Excel, Numbers or Google Sheets.');
    }
    setBusy(false);
  }

  const rows  = useMemo(() => parsed?.rows ?? [], [parsed]);
  const ready = rows.filter(r => r.payload);
  const skipped = rows.length - ready.length;

  async function doImport() {
    const db = getClientDb();
    if (!db || !business || !user) { setError('Database not connected. Please refresh and try again.'); return; }
    setSaving(true); setError('');
    try {
      // writeBatch caps at 500 writes — commit in chunks.
      for (let i = 0; i < ready.length; i += 400) {
        const batch = writeBatch(db);
        ready.slice(i, i + 400).forEach(r => {
          batch.set(doc(collection(db, 'entities')), {
            ...r.payload, businessId: business.id,
            createdAt: serverTimestamp(), updatedAt: serverTimestamp(), createdBy: user.id,
          });
        });
        await batch.commit();
      }
      onImported(ready.length);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed. Check your connection and try again.');
    }
    setSaving(false);
  }

  const TH: React.CSSProperties = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', padding: '6px 8px', textAlign: 'left', background: 'var(--surface-2)', position: 'sticky', top: 0 };
  const TD: React.CSSProperties = { fontSize: 12, color: 'var(--text-2)', padding: '6px 8px', borderTop: '1px solid var(--border)' };
  const dash = <span style={{ opacity: 0.4 }}>—</span>;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Import Entities"
      subtitle="Excel file with your customers, suppliers, brokers and service providers"
      size={rows.length > 0 ? 'lg' : 'md'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" disabled={ready.length === 0} loading={saving} onClick={doImport}>
            Import {ready.length || ''} {ready.length === 1 ? 'Entity' : 'Entities'}
          </Button>
        </>
      }
    >
      <input ref={fileRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) readFile(f); }} />

      <div
        role="button" tabIndex={0}
        onClick={() => fileRef.current?.click()}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileRef.current?.click(); } }}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) readFile(f); }}
        style={{
          border: `1.5px dashed ${dragOver ? 'var(--primary)' : 'var(--border)'}`,
          background: dragOver ? 'var(--primary-light)' : 'var(--surface-2)',
          borderRadius: 'var(--r-sm)', padding: '26px 16px', textAlign: 'center', cursor: 'pointer',
          color: 'var(--text-3)', transition: 'background 0.15s, border-color 0.15s',
        }}
      >
        <IconUploadFile />
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', marginTop: 6 }}>
          {busy ? 'Reading…' : fileName || 'Choose or drop an Excel file'}
        </div>
        <div style={{ fontSize: 11.5, marginTop: 2 }}>
          {fileName ? 'Click to choose a different file' : '.xlsx · one company per row on the "Entities" sheet'}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button type="button" className="line-item-link-btn"
          onClick={() => downloadEntitySample().catch(() => setError('Could not create the sample file.'))}>
          Download Sample File
        </button>
      </div>

      {rows.length > 0 && (
        <>
          <p className="form-hint" style={{ marginTop: 12, marginBottom: 8 }}>
            <strong>{ready.length}</strong> ready to import
            {skipped > 0 && <> · <strong style={{ color: 'var(--danger)' }}>{skipped}</strong> will be skipped</>}
          </p>
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', maxHeight: 320, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>Row</th><th style={TH}>Company</th><th style={TH}>Type</th>
                  <th style={TH}>Owner / Contact</th><th style={TH}>Phone</th><th style={TH}>Country</th><th style={TH}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.rowNo} style={r.problem ? { background: 'color-mix(in srgb, var(--danger) 8%, transparent)' } : undefined}>
                    <td style={{ ...TD, fontFamily: 'var(--font-num)' }}>{r.rowNo}</td>
                    <td style={TD}>{r.name || dash}</td>
                    <td style={TD}>{r.types.length ? r.types.map(t => TYPE_LABEL[t]).join(', ') : dash}</td>
                    <td style={TD}>{r.owner || dash}</td>
                    <td style={TD}>{r.phone || dash}</td>
                    <td style={TD}>{r.country || dash}</td>
                    <td style={{ ...TD, fontWeight: 600, color: r.problem ? 'var(--danger)' : 'var(--success)', whiteSpace: 'nowrap' }}>
                      {r.problem || 'New'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {parsed && parsed.warnings.length > 0 && (
        <div className="msg msg-info" style={{ marginTop: 12, fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 140, overflowY: 'auto' }}>
          {parsed.warnings.join('\n')}
        </div>
      )}

      {error && <div className="msg msg-error" style={{ marginTop: 12 }}>{error}</div>}
    </Modal>
  );
}
